import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, ExitCandidateSelect, FormSelect, SegmentedControl } from '@/components/UI';
import { trpc, trpcClient } from '@/api/trpc';
import { showToast } from '@/components/Layout/Toast';
import type { ParameterDiscoveryObjectiveMode } from '@einhaus/project314-types';
import {
    isStrategyExitDiscoveryActive,
    selectObjectiveIntentOutcomeSet,
    type ObjectiveOutcomeSetOption,
    type StrategyExitDiscoveryStatus
} from './objectiveOutcomeSetSelection';

export type ObjectiveMode = ParameterDiscoveryObjectiveMode;
export type RealizedOutcomeMaterializationEntityType = 'Asset' | 'Cryptocurrency' | 'AssetIndex' | 'all';

interface ObjectiveModeControlProps {
    experimentId: number;
    duration: string;
    horizonSeconds: number;
    strategyId?: number | null;
    materializationEntityType?: RealizedOutcomeMaterializationEntityType | null;
    objectiveMode: ObjectiveMode;
    onObjectiveModeChange: (mode: ObjectiveMode) => void;
    realizedOutcomeSetId: number | null;
    onRealizedOutcomeSetIdChange: (id: number | null) => void;
    requireOutcomeSet?: boolean;
    allowPrepareDiscovery?: boolean;
    disabled?: boolean;
    selectId?: string;
    className?: string;
}

const objectiveModeOptions: { value: ObjectiveMode; label: string }[] = [
    { value: 'fixed_horizon', label: 'Fixed Horizon' },
    { value: 'strategy_exit', label: 'Realized' }
];

type ObjectiveIntentInitAction = { kind: 'wait' } | { kind: 'skip' } | { kind: 'apply'; realizedOutcomeSetId: number | null };

// Pure decision for the one-time objective-intent initialization. Kept out of the component so the effect
// stays a thin dispatcher: 'wait' until data is ready, 'skip' when there is nothing to seed or the user has
// already touched the control, 'apply' the strategy_exit mode plus the realized set matching the intent.
const resolveObjectiveIntentInit = (params: {
    intentExitCandidateId: number | null;
    objectivesLoaded: boolean;
    isPristineDefault: boolean;
    readyOutcomeSetsLoading: boolean;
    readyOutcomeSets: ObjectiveOutcomeSetOption[];
}): ObjectiveIntentInitAction => {
    if (!params.objectivesLoaded) return { kind: 'wait' };
    if (params.intentExitCandidateId === null || !params.isPristineDefault) return { kind: 'skip' };
    if (params.readyOutcomeSetsLoading) return { kind: 'wait' };

    const match = selectObjectiveIntentOutcomeSet(params.readyOutcomeSets, params.intentExitCandidateId);

    return { kind: 'apply', realizedOutcomeSetId: match?.id ?? null };
};

// Applies an 'apply' init action via the parent setters. No-op for 'wait'/'skip'.
const applyObjectiveIntentAction = (
    action: ObjectiveIntentInitAction,
    onObjectiveModeChange: (mode: ObjectiveMode) => void,
    onRealizedOutcomeSetIdChange: (id: number | null) => void
): void => {
    if (action.kind !== 'apply') return;

    onObjectiveModeChange('strategy_exit');

    if (action.realizedOutcomeSetId !== null) {
        onRealizedOutcomeSetIdChange(action.realizedOutcomeSetId);
    }
};

// Runs the whole one-time intent init decision + application. Returns true when the init has resolved (applied
// or skipped) and the caller should latch it, false while still waiting on data. Keeps the effect body a single
// branch so this init logic doesn't add cyclomatic complexity to the component.
const maybeApplyObjectiveIntent = (params: {
    hasAppliedIntent: boolean;
    disabled: boolean;
    primaryStrategyExitIntent: { exitCandidateId: number | null } | null;
    objectivesLoaded: boolean;
    objectiveMode: ObjectiveMode;
    realizedOutcomeSetId: number | null;
    readyOutcomeSetsLoading: boolean;
    readyOutcomeSets: ObjectiveOutcomeSetOption[];
    onObjectiveModeChange: (mode: ObjectiveMode) => void;
    onRealizedOutcomeSetIdChange: (id: number | null) => void;
}): boolean => {
    if (params.hasAppliedIntent || params.disabled) return false;

    const action = resolveObjectiveIntentInit({
        intentExitCandidateId: params.primaryStrategyExitIntent?.exitCandidateId ?? null,
        objectivesLoaded: params.objectivesLoaded,
        isPristineDefault: params.objectiveMode === 'fixed_horizon' && params.realizedOutcomeSetId === null,
        readyOutcomeSetsLoading: params.readyOutcomeSetsLoading,
        readyOutcomeSets: params.readyOutcomeSets
    });

    if (action.kind === 'wait') return false;

    applyObjectiveIntentAction(action, params.onObjectiveModeChange, params.onRealizedOutcomeSetIdChange);

    return true;
};

const ObjectiveModeControl = ({
    experimentId,
    duration,
    horizonSeconds,
    objectiveMode,
    onObjectiveModeChange,
    realizedOutcomeSetId,
    onRealizedOutcomeSetIdChange,
    requireOutcomeSet = false,
    allowPrepareDiscovery = false,
    disabled = false,
    selectId = 'realized-outcome-set',
    strategyId = null,
    materializationEntityType = null,
    className = ''
}: ObjectiveModeControlProps) => {
    const queryClient = useQueryClient();
    const isRealized = objectiveMode === 'strategy_exit';
    const [materializeExitCandidateId, setMaterializeExitCandidateId] = useState<number | null>(null);

    // One-time initialization from the experiment's primary objective intent, so downstream screens don't
    // require re-picking the objective + realized set the user already chose at experiment creation.
    const [hasAppliedIntent, setHasAppliedIntent] = useState(false);

    const objectivesQuery = useQuery({
        ...trpc.experiment.listObjectives.queryOptions({ experimentId }),
        enabled: !hasAppliedIntent && !disabled
    });

    const primaryStrategyExitIntent = useMemo(() => {
        const primary = objectivesQuery.data?.find((objective) => objective.isPrimary === 1) ?? null;

        return primary?.mode === 'strategy_exit' ? primary : null;
    }, [objectivesQuery.data]);

    const outcomeSetsInput = { experimentId, duration, horizonSeconds };
    const readyOutcomeSetsQuery = useQuery({
        ...trpc.experiment.listReadyRealizedOutcomeSets.queryOptions(outcomeSetsInput),
        // Load ready sets when Realized is active, or eagerly during intent init so mode + set seed together.
        enabled: isRealized || (!hasAppliedIntent && primaryStrategyExitIntent !== null),
        refetchInterval: (query) => {
            const selectedSet = query.state.data?.outcomeSets.find((set) => set.id === realizedOutcomeSetId);

            return selectedSet && isStrategyExitDiscoveryActive(selectedSet.discoveryStatus) && selectedSet.discoveryWorkUnitCount > 0
                ? 5000
                : false;
        }
    });

    const outcomeSets = readyOutcomeSetsQuery.data?.outcomeSets ?? [];
    const supersededStockExecutionPolicyNotice = readyOutcomeSetsQuery.data?.supersededStockExecutionPolicyNotice ?? null;
    const selectedOutcomeSet = outcomeSets.find((set) => set.id === realizedOutcomeSetId) ?? null;
    const discoveryStatus = selectedOutcomeSet?.discoveryStatus ?? null;
    const discoveryActive = isStrategyExitDiscoveryActive(discoveryStatus);
    const discoveryWaitingForContinuation =
        discoveryActive && discoveryStatus !== 'deleting' && (selectedOutcomeSet?.discoveryWorkUnitCount ?? 0) === 0;
    const discoveryWorkActive = discoveryActive && !discoveryWaitingForContinuation;
    const discoveryReady = discoveryStatus === 'evidence_ready';
    const hasNoReadySets = isRealized && !readyOutcomeSetsQuery.isLoading && outcomeSets.length === 0;
    const showRequireHint = isRealized && requireOutcomeSet && realizedOutcomeSetId === null && outcomeSets.length > 0;
    const canMaterialize = isRealized && strategyId !== null && materializationEntityType !== null && materializeExitCandidateId !== null;

    // Apply the primary strategy_exit intent once (mode + matching realized set), only while the control is
    // still on its pristine default. A user change, or an experiment with no strategy_exit intent, is left as-is.
    useEffect(() => {
        const didResolve = maybeApplyObjectiveIntent({
            hasAppliedIntent,
            disabled,
            primaryStrategyExitIntent,
            objectivesLoaded: objectivesQuery.isSuccess,
            objectiveMode,
            realizedOutcomeSetId,
            readyOutcomeSetsLoading: readyOutcomeSetsQuery.isLoading,
            readyOutcomeSets: outcomeSets,
            onObjectiveModeChange,
            onRealizedOutcomeSetIdChange
        });

        if (!didResolve) return;

        setHasAppliedIntent(true);
    }, [
        hasAppliedIntent,
        disabled,
        objectivesQuery.isSuccess,
        primaryStrategyExitIntent,
        objectiveMode,
        realizedOutcomeSetId,
        readyOutcomeSetsQuery.isLoading,
        outcomeSets,
        onObjectiveModeChange,
        onRealizedOutcomeSetIdChange
    ]);

    const materializeMutation = useMutation({
        mutationFn: () => {
            if (!canMaterialize || strategyId === null || materializationEntityType === null) {
                throw new Error('A valid exit candidate is required.');
            }

            return trpcClient.experiment.materializeStrategyExitOutcomeSet.mutate({
                experimentId,
                entityType: materializationEntityType === 'all' ? null : materializationEntityType,
                duration,
                strategyId,
                exitCandidateId: materializeExitCandidateId,
                spawnMaterializer: true
            });
        },
        onSuccess: async (result) => {
            await queryClient.invalidateQueries({
                queryKey: trpc.experiment.listReadyRealizedOutcomeSets.queryOptions(outcomeSetsInput).queryKey
            });

            await readyOutcomeSetsQuery.refetch();

            if (result.outcomeSet.status === 'ready') {
                onRealizedOutcomeSetIdChange(result.outcomeSet.id);
                showToast(`Realized outcome set #${result.outcomeSet.id} is ready`, 'success');
                return;
            }

            showToast(`Realized outcome set #${result.outcomeSet.id} queued (${result.outcomeSet.status})`, 'success');
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to materialize realized outcome set', 'error');
        }
    });

    const prepareDiscoveryMutation = useMutation({
        mutationFn: () => {
            if (realizedOutcomeSetId === null) {
                throw new Error('Select a realized outcome set before preparing discovery.');
            }

            return trpcClient.experiment.queueParameterDiscovery.mutate({
                experimentId,
                duration,
                horizonSeconds,
                priority: 'high',
                objectiveMode: 'strategy_exit',
                realizedOutcomeSetId
            });
        },
        onSuccess: async (result) => {
            showToast(result.message, result.success ? 'success' : 'warning');
            await queryClient.invalidateQueries({
                queryKey: trpc.experiment.listReadyRealizedOutcomeSets.queryOptions(outcomeSetsInput).queryKey
            });
            await readyOutcomeSetsQuery.refetch();
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to prepare realized parameter discovery', 'error');
        }
    });

    const formatDiscoveryStatus = (status: StrategyExitDiscoveryStatus, workUnitCount: number): string => {
        if (status === null) return 'Discovery not prepared';
        if (status === 'evidence_ready') return 'Discovery ready';
        if (isStrategyExitDiscoveryActive(status) && status !== 'deleting' && workUnitCount === 0) {
            return 'Discovery waiting for preparation';
        }

        return `Discovery ${status.replaceAll('_', ' ')}`;
    };

    const discoveryBadgeVariant =
        discoveryStatus === 'evidence_ready'
            ? 'success'
            : discoveryStatus === 'failed' || discoveryStatus === 'cancelled'
              ? 'danger'
              : discoveryWorkActive
                ? 'info'
                : 'warning';

    const handleModeChange = (mode: ObjectiveMode) => {
        if (mode === 'fixed_horizon') {
            onRealizedOutcomeSetIdChange(null);
        }

        onObjectiveModeChange(mode);
    };

    const handleOutcomeSetChange = (value: string) => {
        onRealizedOutcomeSetIdChange(value === '' ? null : Number(value));
    };

    return (
        <div className={`flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end ${className}`}>
            <div className="flex flex-col gap-1">
                <span className="text-xs text-zinc-400">Objective</span>
                <div className="flex items-center gap-2">
                    <SegmentedControl<ObjectiveMode>
                        disabled={disabled}
                        options={objectiveModeOptions}
                        value={objectiveMode}
                        onChange={handleModeChange}
                    />
                    <Badge variant={isRealized ? 'success' : 'secondary'}>{isRealized ? 'Realized' : 'Fixed horizon'}</Badge>
                </div>
            </div>

            {isRealized && (
                <div className="flex flex-col gap-1">
                    <FormSelect
                        noMargin
                        className="min-w-[220px]"
                        disabled={disabled || readyOutcomeSetsQuery.isLoading || outcomeSets.length === 0}
                        id={selectId}
                        label="Realized Outcome Set"
                        options={[
                            { value: '', label: readyOutcomeSetsQuery.isLoading ? 'Loading…' : 'Select…' },
                            ...outcomeSets.map((set) => ({
                                value: String(set.id),
                                label:
                                    `Set #${set.id} · candidate #${set.exitCandidateId} · strategy #${set.strategyId} · ` +
                                    `${set.executedTradeCount} trades · ` +
                                    `${formatDiscoveryStatus(set.discoveryStatus, set.discoveryWorkUnitCount)}`
                            }))
                        ]}
                        value={String(realizedOutcomeSetId ?? '')}
                        onChange={(e) => handleOutcomeSetChange(e.target.value)}
                    />
                    {hasNoReadySets && <span className="text-xs text-amber-400">No ready realized outcome sets.</span>}
                    {isRealized && supersededStockExecutionPolicyNotice && (
                        <span className="text-xs text-amber-400" role="status">
                            {supersededStockExecutionPolicyNotice}
                        </span>
                    )}
                    {showRequireHint && <span className="text-xs text-amber-400">Select a ready realized outcome set.</span>}
                    {selectedOutcomeSet && (
                        <div className="flex flex-wrap items-center gap-2">
                            <Badge variant={discoveryBadgeVariant}>
                                {formatDiscoveryStatus(discoveryStatus, selectedOutcomeSet.discoveryWorkUnitCount)}
                            </Badge>
                            {selectedOutcomeSet.producerKind === 'experiment_objective' && (
                                <Badge variant="secondary">Experiment objective</Badge>
                            )}
                            {allowPrepareDiscovery && !discoveryReady && (!discoveryActive || discoveryWaitingForContinuation) && (
                                <Button
                                    disabled={disabled || prepareDiscoveryMutation.isPending}
                                    isLoading={prepareDiscoveryMutation.isPending}
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => prepareDiscoveryMutation.mutate()}
                                >
                                    {discoveryStatus === 'failed' || discoveryStatus === 'cancelled'
                                        ? 'Retry discovery'
                                        : discoveryWaitingForContinuation
                                          ? 'Continue preparation'
                                          : 'Prepare discovery'}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            )}

            {isRealized && strategyId !== null && materializationEntityType !== null && (
                <div className="flex items-end gap-2">
                    <ExitCandidateSelect
                        noMargin
                        className="min-w-[280px]"
                        id={`${selectId}-exit-candidate-id`}
                        isDisabled={disabled || materializeMutation.isPending}
                        label="Global exit candidate"
                        mode="single"
                        placeholder="Search candidates…"
                        value={materializeExitCandidateId}
                        onChange={setMaterializeExitCandidateId}
                    />
                    <Button
                        disabled={disabled || !canMaterialize || materializeMutation.isPending}
                        isLoading={materializeMutation.isPending}
                        size="sm"
                        variant="secondary"
                        onClick={() => materializeMutation.mutate()}
                    >
                        Materialize
                    </Button>
                </div>
            )}
        </div>
    );
};

export default ObjectiveModeControl;
